import type { Recording } from './engine';

/**
 * Post-record pass that gives the editor something to look at: a filmstrip
 * taken straight from the encoded keyframes (each one is a random access point,
 * so a single chunk can be decoded on its own) and a peak envelope from the
 * encoded audio. Both are best-effort — if a browser refuses, the timeline
 * still functions.
 */

export interface Thumbnail {
  tsUs: number;
  url: string;
}

export interface IndexResult {
  thumbs: Thumbnail[];
  peaks: Float32Array | null;
  peakCount: number;
}

export interface IndexProgress {
  done: number;
  total: number;
  stage: 'filmstrip' | 'waveform';
}

const PEAK_BUCKETS = 1400;
const MAX_THUMBS = 42;
const THUMB_HEIGHT = 96;

function nextTask(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

async function buildFilmstrip(recording: Recording, onProgress: (progress: IndexProgress) => void): Promise<Thumbnail[]> {
  const decoderConfig = recording.video.meta?.decoderConfig;
  if (!decoderConfig || typeof VideoDecoder === 'undefined') return [];

  const keyframes = recording.keyframes;
  if (keyframes.length === 0) return [];

  const stride = Math.max(1, Math.round(keyframes.length / MAX_THUMBS));
  const picked: number[] = [];
  for (let i = 0; i < keyframes.length; i += stride) picked.push(keyframes[i]);

  const canvas = document.createElement('canvas');
  const height = THUMB_HEIGHT;
  const width = Math.round((recording.width / recording.height) * height);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];

  const thumbs: Thumbnail[] = [];
  let pendingTsUs: number | null = null;
  const decoder = new VideoDecoder({
    output: (frame) => {
      ctx.drawImage(frame, 0, 0, width, height);
      frame.close();
      const tsUs = pendingTsUs;
      if (tsUs === null) return;
      try {
        thumbs.push({ tsUs, url: canvas.toDataURL('image/jpeg', 0.62) });
      } catch {
        // Tainted or too large - skip this still.
      }
    },
    error: () => undefined,
  });

  try {
    decoder.configure({
      codec: decoderConfig.codec,
      description: decoderConfig.description,
      codedWidth: decoderConfig.codedWidth ?? recording.width,
      codedHeight: decoderConfig.codedHeight ?? recording.height,
      hardwareAcceleration: 'no-preference',
      optimizeForLatency: true,
    });

    for (let i = 0; i < picked.length; i += 1) {
      const index = picked[i];
      onProgress({ done: i, total: picked.length, stage: 'filmstrip' });
      try {
        pendingTsUs = recording.video.timeline[index].timestamp;
        decoder.decode(recording.video.chunks[index]);
        await decoder.flush();
      } catch {
        break;
      }
      if (i % 6 === 5) await nextTask();
    }
  } catch {
    // Fall through with whatever stills we managed to grab.
  } finally {
    try {
      decoder.close();
    } catch {
      // already closed
    }
  }

  return thumbs.filter((thumb) => thumb.url).sort((a, b) => a.tsUs - b.tsUs);
}

async function buildWaveform(recording: Recording, onProgress: (progress: IndexProgress) => void): Promise<Float32Array | null> {
  const track = recording.audio;
  const decoderConfig = track?.meta?.decoderConfig;
  if (!track || !decoderConfig || typeof AudioDecoder === 'undefined') return null;

  const durationUs = recording.durationUs || 1;
  const peaks = new Float32Array(PEAK_BUCKETS);
  let decoded = 0;

  const decoder = new AudioDecoder({
    output: (data) => {
      const frames = data.numberOfFrames;
      const channels = data.numberOfChannels;
      const scratch = new Float32Array(data.allocationSize({ planeIndex: 0, format: 'f32-planar' }) / 4);
      let mono = scratch;
      if (channels > 1) {
        data.copyTo(scratch, { planeIndex: 0, format: 'f32-planar' });
        const second = new Float32Array(frames);
        data.copyTo(second, { planeIndex: 1, format: 'f32-planar' });
        mono = new Float32Array(frames);
        for (let i = 0; i < frames; i += 1) mono[i] = (scratch[i] + second[i]) / 2;
      } else {
        data.copyTo(mono, { planeIndex: 0, format: 'f32-planar' });
      }
      const chunkStartUs = data.timestamp;
      const perSampleUs = 1_000_000 / data.sampleRate;
      for (let i = 0; i < frames; i += 1) {
        const tsUs = chunkStartUs + i * perSampleUs;
        const bucket = Math.min(PEAK_BUCKETS - 1, Math.max(0, Math.floor((tsUs / durationUs) * PEAK_BUCKETS)));
        const value = Math.abs(mono[i]);
        if (value > peaks[bucket]) peaks[bucket] = value;
      }
      data.close();
    },
    error: () => undefined,
  });

  try {
    decoder.configure({
      codec: decoderConfig.codec,
      description: decoderConfig.description,
      sampleRate: decoderConfig.sampleRate ?? track.sampleRate,
      numberOfChannels: decoderConfig.numberOfChannels ?? track.channels,
    });

    const total = track.chunks.length;
    for (let i = 0; i < total; i += 1) {
      if (decoder.decodeQueueSize > 60) {
        await new Promise<void>((resolve) => {
          decoder.addEventListener('dequeue', () => resolve(), { once: true });
        });
      }
      if (i % 64 === 0) {
        onProgress({ done: i, total, stage: 'waveform' });
        await nextTask();
      }
      try {
        decoder.decode(track.chunks[i]);
      } catch {
        break;
      }
      decoded += 1;
    }
    await decoder.flush();
  } catch {
    try {
      decoder.close();
    } catch {
      // already closed
    }
    return decoded > 0 ? peaks : null;
  } finally {
    try {
      decoder.close();
    } catch {
      // already closed
    }
  }

  // Normalize so quiet takes still show a readable envelope.
  let max = 0;
  for (const peak of peaks) if (peak > max) max = peak;
  if (max > 0) {
    const scale = max < 0.32 ? 0.32 / max : 1;
    for (let i = 0; i < peaks.length; i += 1) peaks[i] = Math.min(1, peaks[i] * scale);
  }
  return peaks;
}

export async function indexRecording(
  recording: Recording,
  onProgress: (progress: IndexProgress) => void,
): Promise<IndexResult> {
  let thumbs: Thumbnail[] = [];
  let peaks: Float32Array | null = null;
  try {
    thumbs = await buildFilmstrip(recording, onProgress);
  } catch {
    thumbs = [];
  }
  try {
    peaks = await buildWaveform(recording, onProgress);
  } catch {
    peaks = null;
  }
  return { thumbs, peaks, peakCount: peaks ? PEAK_BUCKETS : 0 };
}
